<template>
  <div class="merchant-auth">
    <el-card class="auth-card" shadow="never">
      <div slot="header" class="card-header">
        <h2>{{ isEdit ? $t("changeEnterpriseAuth") : $t("merchantAuth") }}</h2>
      </div>
    </el-card>
    <el-alert
      v-if="isEdit"
      :title="$t('changeAuthTip')"
      type="info"
      show-icon
      :closable="false"
    />

    <!-- 表单开始 -->
    <el-form ref="formRef" :model="formData" :rules="rules" label-width="140px">
      <!-- 基础信息 -->
      <el-card class="mt-10" shadow="never">
        <div slot="header">
          <span>{{ $t("basicInfo1") }}</span>
        </div>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item :label="$t('merchantName')" prop="merchantName">
              <el-input
                v-model="formData.merchantName"
                :placeholder="$t('pleaseInputMerchantName')"
              ></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('creditCode')" prop="creditCode">
              <el-input
                v-model="formData.creditCode"
                :placeholder="$t('pleaseInputValidCreditCode')"
              ></el-input>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item :label="$t('registerAddress')" prop="registerAddress">
              <el-input
                v-model="formData.registerAddress"
                :placeholder="$t('pleaseInputRegisterAddress')"
              ></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('businessAddress')" prop="businessAddress">
              <el-input
                v-model="formData.businessAddress"
                :placeholder="$t('pleaseInputBusinessAddress')"
              ></el-input>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item
              :label="$t('licenseStartDate')"
              prop="licenseStartDate"
            >
              <el-date-picker
                v-model="formData.licenseStartDate"
                type="date"
                :placeholder="$t('selectDate')"
                value-format="yyyy-MM-dd"
              ></el-date-picker>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('licenseEndDate')" prop="licenseEndDate">
              <el-date-picker
                v-model="formData.licenseEndDate"
                type="date"
                :placeholder="$t('selectDate')"
                value-format="yyyy-MM-dd"
              ></el-date-picker>
            </el-form-item>
          </el-col>
        </el-row>
      </el-card>

      <!-- 公司类型（多层级下拉） -->
      <el-card class="mt-10" shadow="never">
        <div slot="header">
          <span>{{ $t("companyType") }}</span>
        </div>
        <el-form-item :label="$t('selectType')" prop="companyType">
          <el-cascader
            :key="$i18n.locale"
            v-model="formData.companyType"
            :options="companyTypeOptions"
            :props="cascaderProps"
            :placeholder="$t('selectCompanyType')"
            @change="handleCompanyTypeChange"
          ></el-cascader>
        </el-form-item>
      </el-card>
      <!-- 营业执照（拖拽上传） -->
      <el-card class="mt-10" shadow="never">
        <div slot="header">
          <span>{{ $t("businessLicense") }}</span>
        </div>
        <el-form-item
          :label="$t('uploadBusinessLicense')"
          prop="businessLicense"
        >
          <div v-if="!businessLicenseFile" class="upload-container">
            <el-upload
              drag
              class="upload-dragger"
              action="#"
              :auto-upload="false"
              :accept="'.jpg,.jpeg,.png'"
              :on-change="
                (file, fileList) =>
                  handleFileSelect('businessLicense', file, fileList)
              "
              :on-remove="handleFileRemove('businessLicense')"
              :before-upload="beforeUpload"
            >
              <i class="el-icon-upload"></i>
              <div class="el-upload__text">
                <em>{{ $t("uploadClickOrDrag") }}</em
                ><br />
                <span class="el-upload__hint">{{ $t("uploadFormatTip") }}</span>
              </div>
            </el-upload>
          </div>
          <div v-else class="upload-preview">
            <el-image
              :src="businessLicenseFile.url"
              fit="contain"
              class="preview-image"
              style="object-fit: contain; max-width: 100%; max-height: 100%"
            ></el-image>
            <el-button
              type="danger"
              size="small"
              @click="removeBusinessLicense"
            >
              {{ $t("delete") }}
            </el-button>
          </div>
        </el-form-item>
      </el-card>

      <!-- 经营许可（拖拽上传） -->
      <el-card class="mt-10" shadow="never">
        <div slot="header">
          <span>{{ $t("businessPermit") }}</span>
        </div>
        <el-form-item :label="$t('uploadBusinessPermit')" prop="businessPermit">
          <div v-if="!businessPermitFile" class="upload-container">
            <el-upload
              drag
              class="upload-dragger"
              action="#"
              :auto-upload="false"
              :accept="'.pdf,.jpg,.jpeg,.png'"
              :on-change="
                (file, fileList) =>
                  handleFileSelect('businessPermit', file, fileList)
              "
              :on-remove="handleFileRemove('businessPermit')"
              :before-upload="beforeUpload"
            >
              <i class="el-icon-upload"></i>
              <div class="el-upload__text">
                <em>{{ $t("uploadClickOrDrag") }}</em
                ><br />
                <span class="el-upload__hint">{{ $t("uploadPdfTip") }}</span>
              </div>
            </el-upload>
          </div>
          <div v-else class="upload-preview">
            <el-image
              :src="businessPermitFile.url"
              fit="contain"
              class="preview-image"
              style="object-fit: contain; max-width: 100%; max-height: 100%"
            ></el-image>
            <el-button type="danger" size="small" @click="removeBusinessPermit">
              {{ $t("delete") }}
            </el-button>
          </div>
        </el-form-item>
      </el-card>

      <!-- 签约人信息 -->
      <el-card class="mt-10" shadow="never">
        <div slot="header">
          <span>{{ $t("signerInfo") }}</span>
        </div>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item :label="$t('signerName')" prop="signerName">
              <el-input
                v-model="formData.signerName"
                :placeholder="$t('pleaseInputSignerName')"
              ></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('idCardNumber')" prop="idCardNumber">
              <el-input
                v-model="formData.idCardNumber"
                :placeholder="$t('pleaseInputIdCard')"
              ></el-input>
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item :label="$t('position')" prop="position">
          <el-select
            v-model="formData.position"
            :placeholder="$t('selectPosition')"
          >
            <el-option
              v-for="item in positionOptions"
              :key="item.id"
              :label="$i18n.locale === 'en' ? item.name_en : item.name"
              :value="item.id"
            ></el-option>
          </el-select>
        </el-form-item>
        <el-form-item :label="$t('idCardPhoto')" prop="idCardFront">
          <el-row :gutter="20">
            <el-col :span="11">
              <div v-if="!idCardFrontFile" class="upload-container">
                <el-upload
                  drag
                  class="upload-dragger"
                  action="#"
                  :auto-upload="false"
                  :accept="'.jpg,.jpeg,.png'"
                  :on-change="
                    (file, fileList) =>
                      handleFileSelect('idCardFront', file, fileList)
                  "
                  :on-remove="handleFileRemove('idCardFront')"
                  :before-upload="beforeUpload"
                >
                  <i class="el-icon-upload"></i>
                  <div class="el-upload__text">
                    <em>{{ $t("idCardFront") }}</em
                    ><br />
                    <span class="el-upload__hint">{{
                      $t("uploadFormatTip")
                    }}</span>
                  </div>
                </el-upload>
              </div>
              <div v-else class="upload-preview">
                <el-image
                  :src="idCardFrontFile.url"
                  fit="contain"
                  class="preview-image"
                  style="object-fit: contain; max-width: 100%; max-height: 100%"
                ></el-image>
                <el-button
                  type="danger"
                  size="small"
                  @click="removeIdCardFront"
                >
                  {{ $t("delete") }}
                </el-button>
              </div>
            </el-col>
            <el-col :span="11">
              <el-form-item prop="idCardBack">
                <div v-if="!idCardBackFile" class="upload-container">
                  <el-upload
                    drag
                    class="upload-dragger"
                    action="#"
                    :auto-upload="false"
                    :accept="'.jpg,.jpeg,.png'"
                    :on-change="
                      (file, fileList) =>
                        handleFileSelect('idCardBack', file, fileList)
                    "
                    :on-remove="handleFileRemove('idCardBack')"
                    :before-upload="beforeUpload"
                  >
                    <i class="el-icon-upload"></i>
                    <div class="el-upload__text">
                      <em>{{ $t("idCardBack") }}</em
                      ><br />
                      <span class="el-upload__hint">{{
                        $t("uploadFormatTip")
                      }}</span>
                    </div>
                  </el-upload>
                </div>
                <div v-else class="upload-preview">
                  <el-image
                    :src="idCardBackFile.url"
                    fit="contain"
                    class="preview-image"
                    style="
                      object-fit: contain;
                      max-width: 100%;
                      max-height: 100%;
                    "
                  ></el-image>
                  <el-button
                    type="danger"
                    size="small"
                    @click="removeIdCardBack"
                  >
                    {{ $t("delete") }}
                  </el-button>
                </div>
              </el-form-item>
            </el-col>
          </el-row>
        </el-form-item>
      </el-card>
    </el-form>

    <!-- 底部按钮 -->
    <div class="footer-btns mt-10">
      <el-button size="normal" class="ml-20" @click="handleCancel">
        {{ $t("cancel") }}
      </el-button>
      <el-button
        type="primary"
        size="normal"
        class="ml-10"
        @click="handleSubmit"
        >{{ isEdit ? $t("submitChange") : $t("submitCheck") }}</el-button
      >
    </div>
  </div>
</template>

<script>
export default {
  name: "MerchantAuth",
  data() {
    return {
      isEdit: false,
      currentAuthId: null,
      formData: {
        merchantName: "",
        creditCode: "",
        registerAddress: "",
        businessAddress: "",
        licenseStartDate: "",
        licenseEndDate: "",
        companyType: [],
        businessLicense: "",
        businessPermit: "",
        signerName: "",
        idCardNumber: "",
        position: "",
        idCardFront: "",
        idCardBack: "",
      },
      // 表单校验规则
      rules: {
        merchantName: [
          {
            required: true,
            message: this.$t("pleaseInputMerchantName"),
            trigger: "blur",
          },
        ],
        creditCode: [
          {
            required: true,
            message: this.$t("pleaseInputCreditCode"),
            trigger: "blur",
          },
          {
            pattern: /^[0-9A-HJ-NPQRTUWXY]{18}$/,
            message: this.$t("pleaseInputValidCreditCode"),
            trigger: "blur",
          },
        ],
        registerAddress: [
          {
            required: true,
            message: this.$t("pleaseInputRegisterAddress"),
            trigger: "blur",
          },
        ],
        businessAddress: [
          {
            required: true,
            message: this.$t("pleaseInputBusinessAddress"),
            trigger: "blur",
          },
        ],
        licenseStartDate: [
          {
            required: true,
            message: this.$t("pleaseSelectLicenseStartDate"),
            trigger: "change",
          },
        ],
        licenseEndDate: [
          {
            required: true,
            message: this.$t("pleaseSelectLicenseEndDate"),
            trigger: "change",
          },
        ],
        companyType: [
          {
            required: true,
            message: this.$t("selectCompanyType"),
            trigger: "change",
          },
        ],
        signerName: [
          {
            required: true,
            message: this.$t("pleaseInputSignerName"),
            trigger: "blur",
          },
        ],
        idCardNumber: [
          {
            required: true,
            message: this.$t("pleaseInputIdCard"),
            trigger: "blur",
          },
          {
            pattern: /(^\d{15}$)|(^\d{18}$)|(^\d{17}(\d|X|x)$)/,
            message: this.$t("pleaseInputValidIdCard"),
            trigger: "blur",
          },
        ],
        position: [
          {
            required: true,
            message: this.$t("pleaseSelectPosition"),
            trigger: "change",
          },
        ],
        businessLicense: [
          {
            required: true,
            message: this.$t("pleaseUploadBusinessLicense"),
            trigger: "change",
          },
        ],
        idCardFront: [
          {
            required: true,
            message: this.$t("pleaseUploadIdCardFront"),
            trigger: "change",
          },
        ],
        idCardBack: [
          {
            required: true,
            message: this.$t("pleaseUploadIdCardBack"),
            trigger: "change",
          },
        ],
      },
      companyTypeOptions: [], // 公司类型选项
      positionOptions: [], // 职位选项
      // 文件上传相关
      businessLicenseFile: null,
      businessPermitFile: null,
      idCardFrontFile: null,
      idCardBackFile: null,
      uploadResults: {
        businessLicense: null,
        businessPermit: null,
        idCardFront: null,
        idCardBack: null,
      },
      uploadProgress: {
        businessLicense: 0,
        businessPermit: 0,
        idCardFront: 0,
        idCardBack: 0,
      },
      isUploading: false,
      agreeProtocol: false,
    };
  },
  props: {
    propAuthId: {
      type: Number,
      default: null,
    },
  },
  created() {
    // 判断是否编辑状态
    this.isEdit = this.$route.query.edit;
    this.currentAuthId = this.$route.query.id;
    // 获取静态数据
    this.getStaticData();

    // 编辑时获取认证信息
    if (this.isEdit && this.currentAuthId) {
      this.fetchAuthInfo();
    }
  },
  computed: {
    cascaderProps() {
      return {
        expandTrigger: "hover",
        value: "id",
        label: this.$i18n.locale === "en" ? "name_en" : "name",
        children: "children",
      };
    },
  },
  methods: {
    // 获取静态数据
    getStaticData() {
      this.$get("/get_static", { type: "user_duties,company_type" }).then(
        (res) => {
          if (res.data.code === 1) {
            this.companyTypeOptions = res.data.data.company_type;
            this.positionOptions = res.data.data.user_duties;
          } else {
            this.$message.error(res.data.msg);
          }
        },
      );
    },
    // 获取认证信息
    fetchAuthInfo() {
      this.$get("/merchant_auth", { id: this.currentAuthId })
        .then((res) => {
          if (res.data.code === 1 && res.data.data) {
            const authData = res.data.data;
            this.formData = {
              merchantName: authData.enterpriseName,
              creditCode: authData.creditCode,
              registerAddress: authData.registerAddress,
              businessAddress: authData.businessAddress,
              licenseStartDate: authData.licenseStartDate,
              licenseEndDate: authData.licenseEndDate,
              companyType: this.formatCompanyType(authData.companyType),
              signerName: authData.legalRepName,
              idCardNumber: authData.handlerIdCard,
              position:
                Number(authData.position) ||
                (this.positionOptions.length > 0
                  ? this.positionOptions[0].id
                  : ""),
              businessLicense: authData.businessLicense || "",
              businessPermit: authData.businessPermit || "",
              idCardFront: authData.idCardFront || "",
              idCardBack: authData.idCardBack || "",
            };

            // 回显营业执照
            if (authData.businessLicense) {
              this.businessLicenseFile = {
                url: authData.businessLicense,
                name: this.$t("businessLicense"),
                file: null,
                type: "businessLicense",
              };
            }

            // 回显经营许可
            if (authData.businessPermit) {
              this.businessPermitFile = {
                url: authData.businessPermit,
                name: this.$t("businessPermit"),
                file: null,
                type: "businessPermit",
              };
            }

            // 回显身份证正面
            if (authData.idCardFront) {
              this.idCardFrontFile = {
                url: authData.idCardFront,
                name: this.$t("idCardFront"),
                file: null,
                type: "idCardFront",
              };
            }

            // 回显身份证反面
            if (authData.idCardBack) {
              this.idCardBackFile = {
                url: authData.idCardBack,
                name: this.$t("idCardBack"),
                file: null,
                type: "idCardBack",
              };
            }
          } else {
            this.$message.error(res.data.msg || this.$t("getAuthInfoFailed"));
            setTimeout(() => {
              this.$router.go(-1);
            }, 1500);
          }
        })
        .catch((err) => {
          console.error(this.$t("getAuthInfoFailed"), err);
          this.$message.error(this.$t("getAuthInfoFailedRetry"));
        });
    },
    // 格式化公司类型
    formatCompanyType(companyTypeStr) {
      if (!companyTypeStr) return [];
      return companyTypeStr.split("/").map(Number);
    },
    // 公司类型选择事件
    handleCompanyTypeChange(val) {
      const lastOption = this.findOptionByValue(val, this.companyTypeOptions);
      if (lastOption && lastOption.children && lastOption.children.length > 0) {
        this.$message.warning(this.$t("selectLastLevel"));
        this.formData.companyType = [];
      }
    },
    // 递归查找级联选项
    findOptionByValue(values, options) {
      if (!values || values.length === 0 || !options) return null;
      for (const option of options) {
        if (option.id === values[0]) {
          if (values.length === 1) {
            return option;
          } else {
            return this.findOptionByValue(
              values.slice(1),
              option.children || [],
            );
          }
        }
      }
      return null;
    },
    // 上传前校验
    beforeUpload(file) {
      const isJPG =
        file.type === "image/jpeg" ||
        file.type === "image/jpg" ||
        file.type === "image/png";
      const isPDF = file.type === "application/pdf";
      let maxSize = 5 * 1024 * 1024;
      const isLtMaxSize = file.size / 1024 / 1024 < maxSize;

      if (!(isJPG || isPDF)) {
        this.$message.error(this.$t("onlyAllowJpgPngPdf"));
        return false;
      }
      if (!isLtMaxSize) {
        this.$message.error(this.$t("fileSizeLimit"));
        return false;
      }
      return true;
    },
    // 文件选择事件
    handleFileSelect(fileType, file) {
      const localUrl = URL.createObjectURL(file.raw);
      this.uploadProgress[fileType] = 0;
      if (fileType === "businessLicense") {
        this.businessLicenseFile = {
          name: file.name,
          url: localUrl,
          file: file.raw,
          type: fileType,
        };
        this.formData.businessLicense = localUrl;
      } else if (fileType === "businessPermit") {
        this.businessPermitFile = {
          name: file.name,
          url: localUrl,
          file: file.raw,
          type: fileType,
        };
        this.formData.businessPermit = localUrl;
      } else if (fileType === "idCardFront") {
        this.idCardFrontFile = {
          name: file.name,
          url: localUrl,
          file: file.raw,
          type: fileType,
        };
        this.formData.idCardFront = localUrl;
      } else if (fileType === "idCardBack") {
        this.idCardBackFile = {
          name: file.name,
          url: localUrl,
          file: file.raw,
          type: fileType,
        };
        this.formData.idCardBack = localUrl;
      }
      this.$forceUpdate();
    },
    // 文件移除事件
    handleFileRemove(fileType) {
      return (file) => {
        this.uploadResults[fileType] = null;
        this.uploadProgress[fileType] = 0;
        if (fileType === "businessLicense") {
          this.businessLicenseFile = null;
          this.formData.businessLicense = "";
        } else if (fileType === "businessPermit") {
          this.businessPermitFile = null;
          this.formData.businessPermit = "";
        } else if (fileType === "idCardFront") {
          this.idCardFrontFile = null;
        } else if (fileType === "idCardBack") {
          this.idCardBackFile = null;
        }
        this.$forceUpdate();
        this.$message.info(
          `${file.name || this.$t("file")}${this.$t("deleted")}`,
        );
      };
    },
    // 移除营业执照
    removeBusinessLicense() {
      if (this.businessLicenseFile) {
        this.handleFileRemove("businessLicense")(this.businessLicenseFile);
      }
    },
    // 移除经营许可
    removeBusinessPermit() {
      if (this.businessPermitFile) {
        this.handleFileRemove("businessPermit")(this.businessPermitFile);
      }
    },
    // 移除身份证正面
    removeIdCardFront() {
      if (this.idCardFrontFile) {
        this.handleFileRemove("idCardFront")(this.idCardFrontFile);
      }
    },
    // 移除身份证反面
    removeIdCardBack() {
      if (this.idCardBackFile) {
        this.handleFileRemove("idCardBack")(this.idCardBackFile);
      }
    },
    // 保存草稿
    handleSaveDraft() {
      const draftData = {
        ...this.formData,
        uploadResults: this.uploadResults,
        businessLicenseFile: this.businessLicenseFile,
        businessPermitFile: this.businessPermitFile,
        idCardFrontFile: this.idCardFrontFile,
        idCardBackFile: this.idCardBackFile,
        saveTime: new Date().toISOString(),
      };
      localStorage.setItem("merchantAuthDraft", JSON.stringify(draftData));
      this.$message.success(this.$t("draftSaved"));
    },
    // 取消
    handleCancel() {
      this.$router.go(-1);
    },
    // 提交表单
    async handleSubmit() {
      // 防止重复提交
      if (this.isUploading) {
        this.$message.warning(this.$t("uploading"));
        return;
      }
      this.$refs.formRef.validate(async (valid) => {
        // 表单校验不通过
        if (!valid) {
          this.$message.error(this.$t("fillRequiredInfo"));
          return;
        }
        // 检查必填文件
        const requiredFiles = [
          { type: "businessLicense", file: this.businessLicenseFile },
          { type: "idCardFront", file: this.idCardFrontFile },
          { type: "idCardBack", file: this.idCardBackFile },
        ];
        const missingFiles = requiredFiles.filter((item) => !item.file);
        if (missingFiles.length > 0) {
          this.$message.error(this.$t("uploadRequiredFiles"));
          return;
        }
        // 筛选需要上传的文件
        const filesToUpload = [];
        if (this.businessLicenseFile && !this.uploadResults.businessLicense) {
          filesToUpload.push({
            type: "businessLicense",
            file: this.businessLicenseFile.file,
          });
        }
        if (this.businessPermitFile && !this.uploadResults.businessPermit) {
          filesToUpload.push({
            type: "businessPermit",
            file: this.businessPermitFile.file,
          });
        }
        if (this.idCardFrontFile && !this.uploadResults.idCardFront) {
          filesToUpload.push({
            type: "idCardFront",
            file: this.idCardFrontFile.file,
          });
        }
        if (this.idCardBackFile && !this.uploadResults.idCardBack) {
          filesToUpload.push({
            type: "idCardBack",
            file: this.idCardBackFile.file,
          });
        }
        // 存在需要上传的文件
        if (filesToUpload.length > 0) {
          this.isUploading = true;
          const loading = this.$loading({
            lock: true,
            text: this.$t("uploading"),
            spinner: "el-icon-loading",
            background: "rgba(0, 0, 0, 0.7)",
          });
          try {
            // 逐个上传文件
            for (const { type, file } of filesToUpload) {
              if (file != null) {
                await this.uploadFile(type, file);
              }
            }
            // 上传完成提交表单
            this.submitForm();
          } catch (error) {
            console.error(this.$t("fileUploadFailed"), error);
          } finally {
            this.isUploading = false;
            loading.close();
          }
        } else {
          // 无需上传直接提交
          this.submitForm();
        }
      });
    },
    // 上传文件
    uploadFile(fileType, file) {
      return new Promise((resolve, reject) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("fileType", fileType);
        const config = {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total,
            );
            this.uploadProgress[fileType] = percentCompleted;
          },
        };
        this.uploadProgress[fileType] = 0;
        this.$post("/merchant_file_upload", formData, config)
          .then((res) => {
            if (res.data.code === 1) {
              this.uploadResults[fileType] = {
                file,
                res: res.data,
                fileList: [file],
              };
              if (fileType === "businessLicense") {
                this.businessLicenseFile.url = res.data.data.fileUrl;
                this.formData.businessLicense = res.data.data.fileUrl;
              } else if (fileType === "businessPermit") {
                this.businessPermitFile.url = res.data.data.fileUrl;
                this.formData.businessPermit = res.data.data.fileUrl;
              } else if (fileType === "idCardFront") {
                this.idCardFrontFile.url = res.data.data.fileUrl;
              } else if (fileType === "idCardBack") {
                this.idCardBackFile.url = res.data.data.fileUrl;
              }
              resolve(res.data);
            } else {
              // 上传失败
              this.$message.error(res.data.msg || this.$t("uploadFailed"));
              reject(new Error(res.data.msg || this.$t("uploadFailed")));
            }
          })
          .catch((error) => {
            console.error(this.$t("uploadError"), error);
            this.$message.error(this.$t("uploadError"));
            reject(error);
          });
      });
    },
    // 提交表单数据
    submitForm() {
      const submitData = {
        ...this.formData,
        companyType: this.formData.companyType.join("/"),
        authId: this.currentAuthId,
        businessLicense: this.businessLicenseFile
          ? {
              name: this.businessLicenseFile.name,
              url: this.businessLicenseFile.url,
            }
          : null,
        businessPermit: this.businessPermitFile
          ? {
              name: this.businessPermitFile.name,
              url: this.businessPermitFile.url,
            }
          : null,
        idCardFront: this.idCardFrontFile
          ? { name: this.idCardFrontFile.name, url: this.idCardFrontFile.url }
          : null,
        idCardBack: this.idCardBackFile
          ? { name: this.idCardBackFile.name, url: this.idCardBackFile.url }
          : null,
        submitTime: new Date().toISOString(),
      };
      // 判断新增或编辑接口
      const apiUrl = this.isEdit ? "/merchant_update" : "/merchant_submit";
      this.$post(apiUrl, submitData).then((res) => {
        if (res.data.code === 1) {
          this.$message.success(
            res.data.msg ||
              (this.isEdit
                ? this.$t("changeSubmitSuccess")
                : this.$t("submitSuccess")),
          );
          // 重置表单
          this.$refs.formRef.resetFields();
          this.businessLicenseFile = null;
          this.businessPermitFile = null;
          this.idCardFrontFile = null;
          this.idCardBackFile = null;
          this.uploadResults = {
            businessLicense: null,
            businessPermit: null,
            idCardFront: null,
            idCardBack: null,
          };
          this.uploadProgress = {
            businessLicense: 0,
            businessPermit: 0,
            idCardFront: 0,
            idCardBack: 0,
          };
          // 跳转列表页
          setTimeout(() => {
            this.$router.push({
              path: "/about-me/merchant-auth",
              query: { refresh: true },
            });
          }, 1000);
        } else {
          this.$message.error(this.$t("operationFailed"));
        }
      });
    },
  },
  mounted() {
    // 读取本地草稿
    const draftData = localStorage.getItem("merchantAuthDraft");
    if (draftData) {
      try {
        const parsedData = JSON.parse(draftData);
        this.formData = { ...this.formData, ...parsedData };
        if (parsedData.uploadResults)
          this.uploadResults = parsedData.uploadResults;
        if (parsedData.businessLicenseFile)
          this.businessLicenseFile = parsedData.businessLicenseFile;
        if (parsedData.businessPermitFile)
          this.businessPermitFile = parsedData.businessPermitFile;
        if (parsedData.idCardFrontFile)
          this.idCardFrontFile = parsedData.idCardFrontFile;
        if (parsedData.idCardBackFile)
          this.idCardBackFile = parsedData.idCardBackFile;
        this.$message.info(this.$t("draftRestored"));
      } catch (error) {
        console.error(this.$t("draftRestoreFailed"), error);
      }
    }
  },
};
</script>

<style scoped>
::v-deep.auth-card > .el-card__header {
  padding: 0px 16px;
}

::v-deep .el-upload,
::v-deep .el-upload-dragger {
  width: 100%;
}

.el-card.auth-card {
  border: none;
}
.page-title {
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 10px;
}
.mt-10 {
  margin-top: 10px;
}
.footer-btns {
  text-align: center;
  padding: 15px 0;
}
.upload-dragger {
  width: 100%;
}
.upload-container {
  width: 100%;
}
.preview-image {
  max-width: 100%;
  max-height: 200px;
  margin-bottom: 10px;
  object-fit: contain;
  object-position: center;
}

.upload-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px 0;
  border: 1px dashed #dcdfe6;
  border-radius: 4px;
  height: 250px;
  overflow: hidden;
}

@media (max-width: 768px) {
  .el-col {
    flex: 1;
    max-width: 100% !important;
    width: 100% !important;
  }
}
</style>
